import React from 'react';
import PropTypes from 'prop-types';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import range from 'lodash/range';
import get from 'lodash/get';
import {
  Button,
  ButtonVariant,
  Checkbox,
  Icon,
  IconName,
  Modal,
} from '@usealloy/component-library';
import styled from 'styled-components';
import EvaluationCompleteSVG from '~/evaluations/EvaluationCompleteSVG';
import { ajaxFetch } from '~/helpers/api';
import { routes } from '~/helpers/routes';
import { formattedFormData } from '~/helpers/formData';
import withDashboardHooksHOC from '~/helpers/WithDashboardHooksHOC';
import RunEvaluationForm, {
  RunEvaluationFormPropTypes,
  RunEvaluationFormDefaultProps,
} from '~/evaluations/RunEvaluationForm';
import MultiStepModal from '~/evaluations/form/MultiStepModal';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';


const getFormData = response => {
  // meta object does not get carried over from supplied to formatted,
  // include supplied here to include meta.
  const data = response.formatted ? { ...response.supplied, ...response.formatted } : {};
  return data;
};


const CheckboxWrapper = styled.div`
  label {
    margin-bottom: 0;
  }
`;


const RerunEvaluationModalPropTypes = {
  groupId: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  isChildRun: PropTypes.bool,
  isSwitchWorkflowEnabled: PropTypes.bool,
  doPassOriginalEvaluationToken: PropTypes.bool,
  ...RunEvaluationFormPropTypes,
};

const RerunEvaluationModalDefaultProps = {
  ...RunEvaluationFormDefaultProps,
  isSwitchWorkflowEnabled: true,
  isRerun: true,
};

class RerunEvaluationModal extends RunEvaluationForm {
  static handleReloadPage() {
    window.location.reload();
  }


  constructor(props) {
    super(props);

    this.renderSettingsFields = this.renderSettingsFields.bind(this);

    const formData = getFormData(props.evaluation.final_response);
    this.hasMetaFormData = get(props, 'evaluation.final_response.supplied')
      && typeof props.evaluation.final_response.supplied.meta === 'object';

    this.state.formData = formData;
    if (formData.representatives && formData.representatives.length > 1) {
      // If multiple representatives, expand all by default
      range(formData.representatives.length).forEach(index => {
        this.state.expanded[`representatives_${index}`] = true;
      });
    }

    this.state.externalGroupId = props.groupId || 0;
    this.state.customAppVersion = false;
    this.state.settings.bypassFormatter = false;

    this.getApplicationFields({
      application_token: props.evaluation.application_token,
      public_version: props.evaluation.public_version,
    });
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps = newProps => {
    if (newProps.evaluation.evaluation_token !== this.props.evaluation.evaluation_token) {
      const formData = getFormData(newProps.evaluation.final_response);
      this.setState({
        // formData is formatted data from the selected evaluation
        formData,
        // other state reset to false
        customAppVersion: false,
        applicationId: false,
        applicationPublicVersion: false,
      });

      this.getApplicationFields({
        application_token: newProps.evaluation.application_token,
        public_version: newProps.evaluation.public_version,
      });
    }

    // the formatted data modal was just put into child run mode, so clear the form's data
    if (newProps.isChildRun && newProps.isChildRun !== this.props.isChildRun) {
      this.state.formData = {};
    }
  };


  handleCloseModal = () => {
    this.setState({
      formData: getFormData(this.props.evaluation.final_response),
      customAppVersion: false,
      applicationId: false,
      applicationPublicVersion: false,
      newEvaluationUrl: '',
      errorMessage: '',
    }, () => {
      this.props.toggleModal(false);
      this.getApplicationFields({
        application_token: this.props.evaluation.application_token,
        public_version: this.props.evaluation.public_version,
      });
    });
  };


  handleUseCustomAppVersion = useCustomAppVersion => {
    const newState = { customAppVersion: useCustomAppVersion };
    if (!newState.customAppVersion) {
      newState.applicationId = false;
      newState.applicationPublicVersion = false;
    }
    this.setState(newState, () => {
      if (!newState.customAppVersion) {
        this.getApplicationFields({
          application_token: this.props.evaluation.application_token,
          public_version: this.props.evaluation.public_version,
        });
      }
    });
  };


  // ajax submission that handles all the necessary post info
  handleRunEvaluation = event => {
    event.preventDefault();
    const { errors } = this.state;

    if (errors.required.length === 0 && !errors.or) {
      const applicationToken = this.state.customAppVersion
        ? this.props.applications[this.state.applicationId].application_token
        : this.props.evaluation.application_token;
      const applicationVersion = this.state.customAppVersion
        ? this.state.applicationPublicVersion : this.props.evaluation.public_version;

      // cosmetic variables
      this.setState({ submissionInProgress: true });

      // If Evaluation was run with metadata, rerun it with the same metadata
      const formData = {
        ...(this.state.includeExtraData ? this.state.extraData : {}),
        ...this.state.formData,
      };
      if (this.hasMetaFormData) {
        formData.meta = {
          ...(this.state.extraData.meta || {}),
          ...(formData.meta || {}),
        };
      }
      const settings = {
        ...this.state.settings,
        sandboxServices: this.state.settings.sandboxServices.map(s => s.value).join(','),
      };

      const reqBody = {
        application_token: applicationToken,
        application_public_version: applicationVersion,
        evaluation_token: this.props.evaluation.evaluation_token,
        form_data: formattedFormData(formData),
        settings,
        alloy_internal_request_source: 'dashboard_rerun',
        ...(this.state.alloyAuditAccountSlugs
          ? { alloy_audit_account_slugs: this.state.alloyAuditAccountSlugs } : {}
        ),
      };

      if (this.state.externalGroupId !== 0) {
        reqBody.external_group_identifier = this.state.externalGroupId;
      }

      if (this.props.isChildRun || this.props.entity.type === 'group') {
        reqBody.parent_entity_token = this.props.entity.entity_token;
      }

      if (this.props.groupEntityTokenWithWhichToRerunEvaluation) {
        reqBody.parent_entity_token = this.props.groupEntityTokenWithWhichToRerunEvaluation;
        reqBody.do_run_both_entity_and_parent_entity = true;
      }

      if (this.props.doPassOriginalEvaluationToken) {
        reqBody.original_evaluation_token = this.props.evaluation.evaluation_token;
      }

      if (this.props.entity.external_entity_identifier) {
        reqBody.alloy_external_entity_id = this.props.entity.external_entity_identifier;
      }

      const url = this.props.entity.evaluations_data_url
        || routes.entities.evaluation.create({ entityToken: this.props.entity.entity_token });

      ajaxFetch(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reqBody),
        },
      ).then(data => {
        if (['error', 'data_request'].indexOf(data.result) > -1) {
          if (data.response.prompts && Object.keys(data.response.prompts).length) {
            this.setState({
              multiStep: {
                prompts: data.response.prompts,
                patchEvaluationToken: data.response.evaluation_token,
              },
            });
          } else if (
            data.response.error && data.response.error && data.response.error.details
          ) {
            if (data.response.error.details.message) {
              this.setState({ errorMessage: data.response.error.details.message });
            } else if (data.response.error.message) {
              this.setState({ errorMessage: data.response.error.message });
            }
          /**
           * The rerun response can return with `result: "data_request"` but a new eval has
           * been created. In that case we don't want to show an error message like no eval
           * was created, so we can create the new eval link with the data from the response.
           */
          } else if (data.response.entity_token && data.response.evaluation_token) {
            const newEvaluationUrl = routes.entities.evaluation.detail({
              entityToken: data.response.entity_token,
              evaluationToken: data.response.evaluation_token,
            });
            this.setState({ errorMessage: '', newEvaluationUrl });
          } else {
            this.setState({
              errorMessage: 'Something went wrong. Please try again or contact support',
            });
          }
        } else {
          this.props.onRerunEvaluation && this.props.onRerunEvaluation(data);
          this.setState({ errorMessage: '', newEvaluationUrl: data.evaluationUrl });
        }
        this.setState({ submissionInProgress: false });
      });
    } else {
      this.setState({ displayRequiredErrors: true });
      const { addSnackBar } = this.props;
      addSnackBar({
        title: 'Please fix the errors with your form data before rerunning.',
        type: 'danger',
      });
    }
  };


  renderSettingsFields({ doShowSandboxSettings }) {
    const settingsFields = super.renderSettingsFields({ doShowSandboxSettings });
    settingsFields.splice(1, 0, (
      <CheckboxWrapper key={4} className="flex start h-space-16 header-8">
        <Checkbox
          name="bypassFormatter"
          id="bypassFormatter"
          checked={this.state.settings.bypassFormatter}
          onChange={this.handleSettingsChange}
        />
        <label
          id="bypassFormatterLabel"
          htmlFor="bypassFormatter"
        >
          Bypass Alloy Formatter
        </label>
        <Icon id="bypassFormatterLabelIcon" icon={IconName.Question} />
        <Tooltip
          target="bypassFormatterLabelIcon"
          placement="right"
          delay={0}
          modifiers={tooltipZIndexModifiers}
        >
          Disable Alloy&apos;s built-in formatter
        </Tooltip>
      </CheckboxWrapper>
    ));

    // The checkbox below is the only thing that can set customAppVersion / applicationId /
    // applicationPublicVersion, so hiding it is enough to keep the original workflow in the
    // submitted payload — no state reset needed. EntityEvaluationsView enforces this server-side.
    if (this.canSwitchWorkflowOnRerun()) {
      settingsFields.push((
        <div key={5} className="header-8">
          <CheckboxWrapper className="flex start h-space-16">
            <Checkbox
              name="customAppVersion"
              id="customAppVersion"
              checked={this.state.customAppVersion}
              onChange={event => this.handleUseCustomAppVersion(event.target.checked)}
            />
            <label htmlFor="customAppVersion">
              Rerun using a different Workflow
            </label>
          </CheckboxWrapper>

          {this.state.customAppVersion && (
            <div className="switch-workflow-selects header-8">
              {this.renderApplicationSelects()}

              <p className="switch-workflow-caption">
                Switching may cause loss of data
                due to variations in application content and fields
              </p>
            </div>
          )}
        </div>
      ));
    }

    return settingsFields;
  }

  renderPrimaryActionForSuccess = ({ isChildRun, newEvaluationUrl }) => (this.props.doPreventReload
    ? (
      <Button
        variant={ButtonVariant.primary}
        onClick={this.handleCloseModal}
      >
        Close
      </Button>
    )
    : (
      <a href={newEvaluationUrl}>
        <Button variant={ButtonVariant.primary}>
          View {isChildRun ? 'New Child Entity' : 'Evaluation'}
        </Button>
      </a>
    ))

  renderSecondaryActionForSuccess = () => (this.props.doPreventReload
    ? null
    : (
      <Button
        variant={ButtonVariant.secondary}
        onClick={RerunEvaluationModal.handleReloadPage}
      >
        Cancel
      </Button>
    ))

  render = () => {
    const { applicationFields, newEvaluationUrl, multiStep } = this.state;
    const applicationFieldCategories = this.categorizeApplicationFields(applicationFields);
    const {
      entity, isChildRun = true, doPreventReload,
    } = this.props;
    const { entity_token: token } = entity;
    const modalTitle = isChildRun
      ? `Run New Child Entity of ${token}`
      : 'Rerun Entity';

    const doShowSandboxSettings = Boolean(
      this.permissions['READ:Evaluations:rerun_form_sandbox_services_field'],
    );

    return (
      <div>
        {multiStep && (
          <MultiStepModal
            multiStep={multiStep}
            submitMultiStep={this.handleMultiStepSubmit}
            closeModal={this.handleCloseMultiStepModal}
          />
        )}

        <Modal
          id="formatted-data-modal"
          header={modalTitle}
          isOpen={this.props.isOpen && !this.state.multiStep}
          onClose={newEvaluationUrl && !doPreventReload
            ? RerunEvaluationModal.handleReloadPage
            : this.handleCloseModal}
          primaryAction={
            newEvaluationUrl ? this.renderPrimaryActionForSuccess({
              isChildRun,
              newEvaluationUrl,
            }) : (
              <Button
                id="action-submit"
                variant={ButtonVariant.primary}
                disabled={this.state.submissionInProgress
                  || ({}.hasOwnProperty.call(this.state.applicationFields, 'error')
                    || (
                      this.state.customAppVersion
                      && (
                        this.state.applicationId === false
                        || this.state.applicationPublicVersion === false
                      )))}
                onClick={this.handleRunEvaluation}
              >
                {this.state.submissionInProgress
                  ? 'Running Evaluation...' : 'Run Evaluation'}
              </Button>
            )
          }
          secondaryAction={
            newEvaluationUrl ? this.renderSecondaryActionForSuccess() : (
              <Button
                variant={ButtonVariant.secondary}
                onClick={newEvaluationUrl && !doPreventReload
                  ? RerunEvaluationModal.handleReloadPage
                  : this.handleCloseModal}
              >
                Cancel
              </Button>
            )
          }
        >
          {newEvaluationUrl ? (
            <>
              <div className="row header-24">
                <div className="col text-center">
                  <EvaluationCompleteSVG
                    color={this.state.errorMessage === 'partial' ? '#C9412B' : '#463361'}
                  />
                </div>
              </div>

              <div className="row header-24">
                <div className="col">
                  <p>
                    This evaluation has been successfully run
                    {this.state.errorMessage === 'partial'
                      ? ', but there were service failures. ' : '. '}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="formatted-information">
              <form
                ref={f => { this.form = f; }}
                onSubmit={this.handleRerunSubmission}
                autoComplete="stopItChrome"
              >
                {/* Displays an error message if one comes back from the API */}
                {this.state.errorMessage && (
                  <div className="alert alert-danger text-center" role="alert">
                    <strong><Icon icon={IconName.Exclamation} /></strong>
                    {this.state.errorMessage}
                  </div>
                )}

                {{}.hasOwnProperty.call(this.state.applicationFields, 'error') ? (
                  <p>
                    Sorry! Looks like there&apos;s an error with the selected application
                    version. Please contact support@alloy.co if this problem continues.
                  </p>
                ) : (
                  <div>
                    {applicationFieldCategories && !!applicationFieldCategories.length && (
                      <div>
                        {this.renderOrApplicationFields()}

                        {this.renderApplicationFields(applicationFieldCategories)}
                      </div>
                    )}

                    {this.renderSettings({ doShowSandboxSettings })}
                  </div>
                )}
              </form>
            </div>
          )}
        </Modal>
      </div>
    );
  };
}
RerunEvaluationModal.propTypes = RerunEvaluationModalPropTypes;
RerunEvaluationModal.defaultProps = RerunEvaluationModalDefaultProps;

export default withDashboardHooksHOC(RerunEvaluationModal);
